import { useState } from 'react';
import { Plus, X, Play } from 'lucide-react';
import { cn } from '@/utils/cn';

export interface BatchConfigValue { count: number; dimensions: Array<{ name: string; values: string[] }>; }

interface BatchConfigProps {
  initial?: BatchConfigValue | null;
  onConfirm: (config: BatchConfigValue) => void;
  onClose: () => void;
}

/** 预设维度 — 覆盖试玩广告全部可配置维度 */
export const PRESET_DIMENSIONS: Array<{ name: string; examples: string[] }> = [
  { name: '主题', examples: ['僵尸', '海盗', '末日', '森林', '太空', '海洋', '沙漠', '冰雪', '糖果', '水果', '宝石', '魔法', '武侠', '科幻', '都市', '西部', '埃及', '北欧', '赛博朋克', '蒸汽朋克'] },
  { name: '美术风格', examples: ['卡通', '写实', '低多边形', '像素', '扁平', '水彩', '手绘', '赛博朋克', '中国风', '日系动漫', '美式漫画', '3D渲染', '剪纸风', '黏土风'] },
  { name: '难度', examples: ['简单', '中等', '困难', '渐进递增', '随机波动'] },
  { name: '色彩方案', examples: ['暖色调', '冷色调', '高饱和', '低饱和', '暗黑系', '马卡龙', '霓虹', '自然色', '金色奢华', '莫兰迪'] },
  { name: '角色类型', examples: ['动物', '机器人', '人类', '怪物', '食物', '精灵', '忍者', '骑士', '宇航员', '忍者'] },
  { name: '关卡模式', examples: ['无尽', '固定3关', '固定5关', '渐进', '随机生成'] },
  { name: '试玩时长', examples: ['30秒', '35秒', '40秒', '45秒', '60秒'] },
  { name: '引导手势', examples: ['点击', '长按', '滑动', '拖拽', '摇杆', '倾斜', '点击+滑动组合'] },
  { name: 'CTA文案', examples: ['立即下载', '免费下载', '开始游戏', '立即体验', '马上试玩', '免费畅玩', '点击安装', '限时领取'] },
  { name: 'CTA样式', examples: ['渐变胶囊', '3D糖果', '霓虹发光', '毛玻璃', '脉冲跳动', '弹跳', '摇晃', '箭头弹跳'] },
  { name: 'CTA触发时机', examples: ['通关后', '失败后', '30秒后', '达到目标分数', '收集足够金币'] },
  { name: 'HUD样式', examples: ['横条', '极简', '胶囊', '进度条', '圆形', '爱心', '星级评分', '无'] },
  { name: '结束画面', examples: ['简洁', '卡片', '庆祝', '烟花', '淡入', '上滑', '缩放', '彩纸纷飞'] },
  { name: '背景类型', examples: ['静态图', '渐变色', '滚动视差', '多层视差', '循环动画', '粒子背景'] },
  { name: '粒子特效', examples: ['开启', '关闭', '仅关键节点', '大量'] },
  { name: '音效配置', examples: ['全开', '关闭', '仅点击音', '仅BGM', '点击+BGM', '点击+成功+失败'] },
  { name: '背景音乐', examples: ['轻快', '紧张', '史诗', '电子', '古典', '自然', '爵士', '复古电子', '无'] },
  { name: '道具系统', examples: ['无道具', '加速', '护盾', '磁铁', '双倍积分', '时间冻结', '炸弹清除'] },
  { name: '动画节奏', examples: ['快节奏', '中等', '慢节奏', '渐进加速', '波浪式'] },
  { name: '计分方式', examples: ['数字累加', '星级评定', '进度条', '金币收集', '百分比'] },
  { name: '字体风格', examples: ['圆体', '黑体', '手写', '像素', '卡通', '哥特', '霓虹发光'] },
  { name: '语言', examples: ['中文', '英文', '日文', '韩文', '繁体中文', '阿拉伯文', '葡萄牙文', '西班牙文', '法文', '德文'] },
  { name: '画面比例', examples: ['竖屏9:16', '横屏16:9', '方形1:1', '全屏'] },
  { name: '开始界面', examples: ['简洁标题', '角色展示', '关卡预览', '操作引导'] },
];

export function BatchConfig({ initial, onConfirm, onClose }: BatchConfigProps): JSX.Element {
  const [count, setCount] = useState(initial?.count ?? 3);
  const [dims, setDims] = useState<Array<{ name: string; values: string[] }>>(initial?.dimensions ?? []);
  const [newName, setNewName] = useState('');
  const [newValues, setNewValues] = useState<Record<string, string>>({});

  const hasDim = (name: string): boolean => dims.some((d) => d.name === name);

  const togglePreset = (name: string, examples: string[]): void => {
    setDims((prev) => {
      const exists = prev.some((d) => d.name === name);
      if (exists) return prev.filter((d) => d.name !== name);
      const maxCount = Math.max(1, count);
      const values = examples.slice(0, Math.min(maxCount, examples.length));
      return [...prev, { name, values }];
    });
  };

  const addDim = (): void => {
    const n = newName.trim();
    if (!n || hasDim(n)) { setNewName(''); return; }
    setDims([...dims, { name: n, values: [] }]);
    setNewName('');
  };

  const addValue = (dimName: string): void => {
    const val = (newValues[dimName] ?? '').trim();
    if (!val) return;
    setDims(dims.map((d) => (d.name === dimName ? { ...d, values: [...d.values, val] } : d)));
    setNewValues({ ...newValues, [dimName]: '' });
  };

  const removeValue = (dimName: string, idx: number): void =>
    setDims(dims.map((d) => (d.name === dimName ? { ...d, values: d.values.filter((_, i) => i !== idx) } : d)));

  const removeDim = (name: string): void => setDims(dims.filter((d) => d.name !== name));

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm" onClick={onClose}>
      <div className="glass-card flex max-h-[82vh] w-full max-w-lg flex-col p-5" onClick={(e) => e.stopPropagation()}>
        <div className="mb-3 flex items-center justify-between">
          <h3 className="text-sm font-semibold text-foreground">批量生成设置</h3>
          <button onClick={onClose} className="icon-btn rounded p-1"><X size={16} /></button>
        </div>

        <div className="flex items-center gap-2">
          <span className="text-xs text-muted-foreground">生成数量</span>
          <input type="number" min={1} max={20} value={count} onChange={(e) => setCount(Math.max(1, Math.min(20, Number(e.target.value) || 1)))}
            className="input-neon w-16 px-2 py-1 text-center text-sm" />
          <span className="text-[10px] text-muted-foreground">（1-20，多维时按笛卡尔积截断）</span>
        </div>

        {/* 预设维度快选 */}
        <div className="mt-3">
          <span className="text-xs font-semibold text-muted-foreground">预设维度</span>
          <div className="mt-1.5 flex flex-wrap gap-1.5">
            {PRESET_DIMENSIONS.map((d) => (
              <button key={d.name} onClick={() => togglePreset(d.name, d.examples)}
                className={cn('rounded-full border px-2.5 py-0.5 text-[11px] transition-colors',
                  hasDim(d.name) ? 'border-primary/60 bg-primary/15 text-primary' : 'border-border/50 text-muted-foreground hover:border-border')}>
                {d.name}
              </button>
            ))}
          </div>
        </div>

        {/* 自定义维度 */}
        <div className="mt-3 flex gap-1">
          <input className="input-neon flex-1 px-2 py-1 text-xs" placeholder="自定义维度名" value={newName}
            onChange={(e) => setNewName(e.target.value)} onKeyDown={(e) => { if (e.key === 'Enter') addDim(); }} />
          <button onClick={addDim} className="icon-btn rounded p-1"><Plus size={14} /></button>
        </div>

        {/* 已选维度值编辑 */}
        <div className="mt-2 flex-1 space-y-2 overflow-y-auto">
          {dims.map((d) => (
            <div key={d.name} className="rounded-lg bg-muted/20 p-2">
              <div className="mb-1 flex items-center justify-between">
                <span className="text-xs font-medium text-foreground">{d.name}</span>
                <button onClick={() => removeDim(d.name)} className="text-muted-foreground hover:text-red-400"><X size={12} /></button>
              </div>
              <div className="flex flex-wrap gap-1">
                {d.values.map((v, i) => (
                  <span key={i} className="inline-flex items-center gap-0.5 rounded bg-primary/10 px-1.5 py-0.5 text-[11px] text-primary">
                    {v}
                    <button onClick={() => removeValue(d.name, i)} className="text-muted-foreground hover:text-red-400">×</button>
                  </span>
                ))}
              </div>
              <div className="mt-1 flex gap-1">
                <input className="input-neon flex-1 px-1.5 py-0.5 text-[11px]" placeholder="新增值"
                  value={newValues[d.name] ?? ''} onChange={(e) => setNewValues({ ...newValues, [d.name]: e.target.value })}
                  onKeyDown={(e) => { if (e.key === 'Enter') addValue(d.name); }} />
                <button onClick={() => addValue(d.name)} className="icon-btn rounded p-0.5"><Plus size={12} /></button>
              </div>
            </div>
          ))}
          {dims.length === 0 && <p className="py-3 text-center text-[11px] text-muted-foreground">未选维度时将由 AI 自动生成差异变体</p>}
        </div>

        <div className="mt-4 flex justify-end gap-2">
          <div className="flex gap-2">
            <button onClick={onClose} className="rounded border border-border px-3 py-1 text-xs text-muted-foreground">取消</button>
            <button onClick={() => onConfirm({ count, dimensions: dims })}
              className="glow-btn flex items-center gap-1 rounded px-3 py-1 text-xs font-medium"><Play size={12} />应用批量配置</button>
          </div>
        </div>
      </div>
    </div>
  );
}
